import React, { useCallback } from 'react';
import PrdcUnitItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/PrdcUnitItem';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { Product as DisplayProduct } from '@wconcept/wconcept-common/models/display/product';
import {
  MolocoAdProducts,
  MolocoAdProductsResult,
} from '@wconcept/wconcept-common/models/display/search';
import useGlobalState from '@hooks/useGlobalState';
import useHistoryRouter from '@hooks/useLinkTo';
import useMyHeartItem from '@hooks/useMyHeartItem';
import { addAsIsMWebDomain } from '@wconcept/wconcept-common/utils/domainUtil';
import { EventProduct } from '@wconcept/wconcept-common/models/event/custom';
import MolocoRecProductList from '@components/pages/common/MolocoRecProductList';
import RecoStyleClipList from '@components/pages/search/searchInit/SearchInitPageContent/RecoStyleClipList';
import { SearchStyleClip } from '@wconcept/wconcept-common/models/renewal/search';
import { getExclusiveEmblemProp } from '@utils/productExclusiveUtil';

interface ExclusiveEmblemRenderOptions {
  showExclusiveEmblem?: boolean;
  getExclusiveEmblem?: (
    product: ExclusiveEmblemSource,
    index: number,
    rank?: number
  ) => boolean | undefined;
}

type ExclusiveEmblemSource = {
  infoTags?: string[];
};

function resolveExclusiveEmblem(
  product: ExclusiveEmblemSource,
  index: number,
  rank: number | undefined,
  options?: ExclusiveEmblemRenderOptions
): boolean | undefined {
  if (!options) {
    return undefined;
  }

  const { showExclusiveEmblem, getExclusiveEmblem } = options;

  if (getExclusiveEmblem) {
    return getExclusiveEmblem(product, index, rank);
  }

  return getExclusiveEmblemProp(product, showExclusiveEmblem);
}

interface UseProductsProps extends ExclusiveEmblemRenderOptions {
  type?: 'all' | 'simple' | 'list' | 'simple-small' | 'simple-lg' | undefined;
  rs?: string | ((index: number) => string);
  products: Product[];
  minCount?: number;
  maxCount?: number;
  className?: string;
  isBrandNameKr?: boolean;
  isStatusCd?: boolean;
  colorType?: 'white' | 'black' | string;
  returnUrl?: string;
  refs?: React.RefObject<HTMLDivElement[]>;
  useRank?: {
    page: number;
    pageSize?: number;
  };
  /** 카드별 광고 뱃지 */
  isAd?: (product: Product, index: number) => boolean;
  isLoginOverride?: boolean;
  priority?: (index: number) => boolean;
  onClickProduct?: (product: Product, index: number) => void;
  onClickOption?: (product: Product, index: number) => void;
  onClickProductHeartOverride?: (
    product: Product,
    active: boolean,
    index: number,
    heartCnt?: number
  ) => Promise<string | null | void> | string | null | void;
  checkColorType?: (index: number) => string | undefined;
  adInsertIndex?: number;
  styleClipInsertIndex?: number;
  adData?: MolocoAdProductsResult;
  totalProducts?: number;
  onClickMolocoProductItem?: (product: MolocoAdProducts, index: number) => void;
  onClickStyleClip?: (value: SearchStyleClip, i: number) => void;
  onClickStyleClipMore?: () => void;
  styleClipData?: SearchStyleClip[];
  isLoading?: boolean;
  error?: Error | null;
  refetch?: () => void;
}

interface UseEventProductsProps extends ExclusiveEmblemRenderOptions {
  type?: 'all' | 'simple' | 'list' | 'simple-small' | 'simple-lg' | undefined;
  rs?: string;
  products: EventProduct[];
  minCount?: number;
  maxCount?: number;
  isBrandNameKr?: boolean;
  isStatusCd?: boolean;
  colorType?: 'white' | 'black';
  returnUrl?: string;
  useRank?: {
    page: number;
  };
  priority?: (index: number) => boolean;
  onClickEventProduct?: (product: EventProduct, index: number) => void;
  onClickOption?: (product: EventProduct, index: number) => void;
  checkColorType?: (index: number) => string | undefined;
}
interface UseDisplayProductsProps extends ExclusiveEmblemRenderOptions {
  type?: 'all' | 'simple' | 'list' | 'simple-small' | 'simple-lg' | undefined;
  products: DisplayProduct[];
  minCount?: number;
  maxCount?: number;
  isBrandNameKr?: boolean;
  isStatusCd?: boolean;
  colorType?: 'white';
  useRank?: {
    page: number;
    pageSize?: number;
  };
  isItemName?: boolean;
  isLoginOverride?: boolean;
  priority?: (index: number) => boolean;
  rs?: (index: number) => string;
  onClickDisplayProduct?: (product: DisplayProduct, index: number) => void;
  onClickDisplayOption?: (product: DisplayProduct, index: number) => void;
  checkColorType?: (index: number) => string | undefined;
  onClickDisplayHeart?: (
    product: DisplayProduct,
    isActive: boolean,
    index: number,
    heartCnt?: number
  ) => Promise<string | null | void> | string | null | void;
}

function useRenderProducts(
  params?:
    | {
        doAfterAddMyHeart?: (
          product: Product,
          index?: number,
          type?: string
        ) => void;
        doAfterDeleteMyHeart?: (
          product: Product,
          index?: number,
          type?: string
        ) => void;
      }
    | undefined
) {
  const {
    isMobile,
    myHeartList,
    assistData,
    appInfo,
    addMyHeartProductItem,
    deleteMyHeartProdcutItem,
  } = useGlobalState();
  const { linkTo } = useHistoryRouter();

  const { onClickHeart: onClickProductHeart } = useMyHeartItem({
    isLogin: !!assistData?.isLogin,
    doAfterAddMyHeart: (param, product, index, type) => {
      params?.doAfterAddMyHeart?.(product, index, type);
      addMyHeartProductItem(param.itemCd);
    },
    doAfterDeleteMyHeart: (param, product, index, type) => {
      params?.doAfterDeleteMyHeart?.(product, index, type);
      deleteMyHeartProdcutItem(param.itemCd);
    },
  });

  const handleClickProduct = useCallback(
    (
      product: Product,
      index: number,
      onClickProduct?: (product: Product, index: number) => void,
      onClickOption?: (product: Product, index: number) => void
    ) => {
      if (onClickOption) {
        onClickOption?.(product, index);
      } else {
        onClickProduct?.(product, index);
        if (product.landingUrl) {
          linkTo.push({
            url: product.landingUrl,
          });
        }
      }
    },
    [linkTo]
  );

  const handleClickDisplayProduct = useCallback(
    async (
      product: DisplayProduct,
      index: number,
      onClickProduct?: (product: DisplayProduct, index: number) => void
    ) => {
      onClickProduct?.(product, index);
      if (product.webViewUrl) {
        linkTo.push({
          url: await addAsIsMWebDomain(product.webViewUrl),
        });
      }
    },
    [linkTo]
  );
  const handleClickHeart = useCallback(
    async (
      product: Product,
      active: boolean,
      index: number,
      type?: string,
      targetUrl?: string
    ) =>
      onClickProductHeart?.(
        product,
        active,
        index,
        type,
        undefined,
        undefined,
        undefined,
        targetUrl
      ),
    [onClickProductHeart]
  );

  const handleClickEventProduct = useCallback(
    async (
      product: EventProduct,
      index: number,
      onClickProduct?: (product: EventProduct, index: number) => void,
      onClickOption?: (product: EventProduct, index: number) => void
    ) => {
      if (onClickOption) {
        onClickOption?.(product, index);
      } else {
        onClickProduct?.(product, index);
      }
      if (product.webViewUrl) {
        linkTo.push({
          url: await addAsIsMWebDomain(product.webViewUrl),
        });
      }
    },
    [linkTo]
  );

  const renderProducts = useCallback(
    ({
      type = 'simple',
      className,
      rs,
      products = [],
      minCount,
      maxCount,
      isBrandNameKr,
      isStatusCd = true,
      colorType,
      returnUrl,
      refs,
      useRank,
      isAd,
      isLoginOverride,
      priority,
      onClickProduct,
      onClickOption,
      onClickProductHeartOverride,
      checkColorType,
      adInsertIndex,
      styleClipInsertIndex,
      adData,
      onClickMolocoProductItem,
      onClickStyleClip,
      onClickStyleClipMore,
      styleClipData,
      isLoading,
      error,
      refetch,
      showExclusiveEmblem,
      getExclusiveEmblem,
    }: UseProductsProps) => {
      const filteredProducts = () => {
        if (maxCount && products.length > maxCount) {
          return products.slice(0, maxCount);
        }
        if (minCount && products.length < minCount) {
          return [];
        }
        return products;
      };

      // 몰로코 광고 컴포넌트 렌더링 함수
      const renderMolocoAd = (key: string) => {
        if (!adData?.items || !isMobile) return null;

        return (
          <MolocoRecProductList
            key={key}
            className="corner-recommend-similar"
            list={adData.items}
            isNativeAppBoth={appInfo?.isNativeAppBoth ?? false}
            title="같이 보면 좋을 상품"
            showExclusiveEmblem={showExclusiveEmblem}
            onClickItem={(product, index) => {
              onClickMolocoProductItem?.(product, index);
            }}
            onClickHeart={(product, active, index) =>
              handleClickHeart?.(product, active, index, 'moloco')
            }
          />
        );
      };

      // 스타일클립 광고 컴포넌트 렌더링 함수
      const renderStyleClipAd = (key: string) => {
        if (!styleClipData || !isMobile) return null;
        return (
          <RecoStyleClipList
            key={key}
            onStyleClipClick={(value, i) => onClickStyleClip?.(value, i)}
            onClickStyleClipMore={() => onClickStyleClipMore?.()}
            styleClipData={styleClipData ?? []}
            isLoading={isLoading ?? false}
            error={error ?? null}
            refetch={refetch ?? (() => {})}
          />
        );
      };

      const productsToRender = filteredProducts();
      const result: React.ReactElement[] = [];

      productsToRender.forEach((v: Product, i: number) => {
        // 스타일클립 인덱스에 도달했을 때 추천스타일링 컴포넌트 추가
        if (styleClipInsertIndex !== undefined && i === styleClipInsertIndex) {
          const styleClipComponent = renderStyleClipAd(`video_ad_${i}`);
          if (styleClipComponent) {
            result.push(styleClipComponent);
          }
        }
        // 광고 삽입 인덱스에 도달했을 때 광고 컴포넌트 추가
        if (
          adInsertIndex !== undefined &&
          i === adInsertIndex &&
          adData?.items &&
          adData?.items.length > 0
        ) {
          const adComponent = renderMolocoAd(`ad_${v.itemCd}`);
          if (adComponent) {
            result.push(adComponent);
          }
        }

        const productIndex = useRank
          ? useRank.page * (useRank.pageSize ?? productsToRender.length) + i
          : i;
        const rank = useRank ? productIndex + 1 : undefined;

        result.push(
          <PrdcUnitItem
            isDesktop={!isMobile}
            type={type}
            className={className}
            rs={typeof rs === 'function' ? rs(i) : rs}
            priority={priority?.(i)}
            colorType={colorType || checkColorType?.(i)}
            exclusive={resolveExclusiveEmblem(v, i, rank, {
              showExclusiveEmblem,
              getExclusiveEmblem,
            })}
            ref={el => {
              if (el) {
                if (refs && refs.current) {
                  // eslint-disable-next-line no-param-reassign
                  refs.current[i] = el;
                }
              }
            }}
            // eslint-disable-next-line react/no-array-index-key
            key={`${i}_${v.itemCd}`}
            itemCd={v?.itemCd}
            isAd={isAd?.(v, i)}
            rank={useRank ? productIndex + 1 : undefined}
            isLogin={
              isLoginOverride !== undefined
                ? isLoginOverride
                : !!assistData?.isLogin
            }
            statusCd={isStatusCd ? v?.statusCd : ''}
            finalPrice={v.finalPrice}
            finalDiscountRate={v.finalDiscountRate}
            imageUrl={v.productImageUrl}
            brandNameKr={isBrandNameKr ? v.brandNameKr : ''}
            itemNameSub={type === 'all' ? v.itemNameSub : ''}
            itemName={v.itemName}
            heartCnt={v.heartCnt}
            myHeartItem={
              v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false
            }
            onClick={({ heartCnt }) =>
              handleClickProduct(
                { ...v, heartCnt },
                i,
                onClickProduct,
                onClickOption
              )
            }
            onClickHeart={async (itemCd, active, heartCnt) =>
              onClickProductHeartOverride
                ? onClickProductHeartOverride(
                    { ...v, heartCnt },
                    active,
                    i,
                    heartCnt
                  )
                : handleClickHeart(
                    { ...v, heartCnt },
                    active,
                    productIndex,
                    undefined,
                    returnUrl
                  )
            }
          />
        );
      });

      // adInsertIndex가 상품 개수와 같을 때 (마지막 상품 다음) 광고 추가
      if (
        adInsertIndex !== undefined &&
        adInsertIndex === productsToRender.length &&
        adData?.items &&
        adData?.items.length > 0
      ) {
        const adComponent = renderMolocoAd('ad_final_moloco');
        if (adComponent) {
          result.push(adComponent);
        }
      }

      // styleClipInsertIndex가 상품 개수와 같을 때 (마지막 상품 다음) 스타일클립 추가
      if (
        styleClipInsertIndex !== undefined &&
        styleClipInsertIndex === productsToRender.length &&
        styleClipData &&
        styleClipData.length > 0
      ) {
        const styleClipComponent = renderStyleClipAd('video_ad_final');
        if (styleClipComponent) {
          result.push(styleClipComponent);
        }
      }
      return result;
    },
    [
      isMobile,
      assistData?.isLogin,
      myHeartList?.product,
      handleClickProduct,
      handleClickHeart,
      appInfo?.isNativeAppBoth,
    ]
  );

  const renderDisplayProducts = useCallback(
    ({
      type = 'simple',
      products = [],
      priority,
      minCount,
      maxCount,
      isBrandNameKr,
      useRank,
      isStatusCd,
      isItemName = true,
      isLoginOverride,
      colorType,
      rs,
      onClickDisplayProduct,
      onClickDisplayOption,
      checkColorType,
      onClickDisplayHeart,
      showExclusiveEmblem,
      getExclusiveEmblem,
    }: UseDisplayProductsProps) => {
      const filteredProducts = () => {
        if (maxCount && products.length > maxCount) {
          return products.slice(0, maxCount);
        }
        if (minCount && products.length < minCount) {
          return [];
        }
        return products;
      };

      const productsToRender = filteredProducts();

      return productsToRender.map((v: DisplayProduct, i: number) => {
        const productIndex = useRank
          ? useRank.page * (useRank.pageSize ?? productsToRender.length) + i
          : i;
        const rank = useRank ? productIndex + 1 : undefined;

        return (
          <PrdcUnitItem
            isDesktop={!isMobile}
            type={type}
            rs={rs?.(i)}
            priority={priority?.(i)}
            colorType={colorType || checkColorType?.(i)}
            exclusive={resolveExclusiveEmblem(v, i, rank, {
              showExclusiveEmblem,
              getExclusiveEmblem,
            })}
            // eslint-disable-next-line react/no-array-index-key
            key={`${i}_${v.itemCd}`}
            itemCd={v.itemCd}
            rank={useRank ? productIndex + 1 : undefined}
            isLogin={
              isLoginOverride !== undefined
                ? isLoginOverride
                : !!assistData?.isLogin
            }
            statusCd={isStatusCd ? v?.statusCd : ''}
            finalPrice={v.finalPrice}
            finalDiscountRate={v.finalDiscountRate}
            imageUrl={v.imageUrlMobile}
            brandNameKr={isBrandNameKr ? v.brandNameKr : ''}
            itemNameSub={type === 'all' ? v.itemNameSub : ''}
            itemName={isItemName ? v.itemName : ''}
            myHeartItem={
              v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false
            }
            heartCnt={v.heartCnt}
            onClick={({ heartCnt }) => {
              if (onClickDisplayOption) {
                onClickDisplayOption({ ...v, heartCnt }, productIndex);
                return;
              }
              handleClickDisplayProduct(
                { ...v, heartCnt },
                productIndex,
                onClickDisplayProduct
              );
            }}
            onClickHeart={async (itemCd, active, heartCnt) =>
              onClickDisplayHeart
                ? onClickDisplayHeart(
                    { ...v, heartCnt },
                    active,
                    productIndex,
                    heartCnt
                  )
                : handleClickHeart({ ...v, heartCnt }, active, productIndex)
            }
          />
        );
      });
    },
    [
      isMobile,
      assistData?.isLogin,
      myHeartList?.product,
      handleClickDisplayProduct,
      handleClickHeart,
    ]
  );

  const renderEventProducts = useCallback(
    ({
      type = 'simple',
      rs,
      products = [],
      minCount,
      maxCount,
      isBrandNameKr,
      isStatusCd = true,
      colorType,
      returnUrl,
      useRank,
      onClickEventProduct,
      onClickOption,
      checkColorType,
      showExclusiveEmblem,
      getExclusiveEmblem,
    }: UseEventProductsProps) => {
      const filteredProducts = () => {
        if (maxCount && products.length > maxCount) {
          return products.slice(0, maxCount);
        }
        if (minCount && products.length < minCount) {
          return [];
        }
        return products;
      };
      return filteredProducts().map((v: EventProduct, i: number) => {
        const rank = useRank
          ? useRank.page * filteredProducts().length + i + 1
          : undefined;

        return (
          <PrdcUnitItem
            isDesktop={!isMobile}
            type={type}
            className="is-renewal"
            rs={rs}
            colorType={colorType || checkColorType?.(i)}
            exclusive={resolveExclusiveEmblem(v, i, rank, {
              showExclusiveEmblem,
              getExclusiveEmblem,
            })}
            // eslint-disable-next-line react/no-array-index-key
            key={`${i}_${v.itemCd}`}
            itemCd={v.itemCd}
            isLogin={!!assistData?.isLogin}
            statusCd={isStatusCd ? v?.statusCd : ''}
            finalPrice={v.finalPrice}
            finalDiscountRate={v.finalDiscountRate}
            imageUrl={v.imageUrlMobile}
            rank={
              useRank
                ? useRank.page * filteredProducts().length + i + 1
                : undefined
            }
            brandNameKr={isBrandNameKr ? v.brandNameKr : ''}
            itemNameSub={type === 'all' ? v.itemNameSub : ''}
            itemName={v.itemName}
            heartCnt={v.heartCnt}
            myHeartItem={
              v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false
            }
            onClick={({ heartCnt }) => {
              if (onClickEventProduct) {
                onClickEventProduct({ ...v, heartCnt }, i);
                return;
              }
              handleClickEventProduct(
                { ...v, heartCnt },
                i,
                onClickEventProduct,
                onClickOption
              );
            }}
            onClickHeart={async (itemCd, active, heartCnt) =>
              handleClickHeart({ ...v, heartCnt }, active, i, returnUrl)
            }
          />
        );
      });
    },
    [
      isMobile,
      assistData?.isLogin,
      myHeartList?.product,
      handleClickEventProduct,
      handleClickHeart,
    ]
  );

  return { renderProducts, renderDisplayProducts, renderEventProducts };
}

export default useRenderProducts;
